import * as $ from 'jquery';
import { serialDisposable, disposableSubscription, emptyDisposable, Lazy, disposable, IDisposable } from 'common/barrels/core';
import { Subject, BehaviorSubject, Observable } from 'common/barrels/rx';
import { endsWith } from 'common/functions/string';
import { maxDefaultZero } from 'common/functions/array';

import { Control } from 'runtime/controls/control';
import { ControlArgs } from 'runtime/controls/controlargs';
import { LookupItem } from 'runtime/data/lookupitem';
import { LookupItemRequested } from 'runtime/events/lookupitemrequested';
import { LookupItemsRequested } from 'runtime/events/lookupitemsrequested';
import { registerBinding } from 'runtime/bindingengine';
import { and } from 'common/functions/observable';
import * as rxui from 'common/rxui/core';

import 'runtime/styles/controls/dropdownlist.less';

import 'jquery-mousewheel';

var maxListHeight = 300;
var listMargin = 20;

class FilterItem { 
    constructor(public filter: string, public match: LookupItem) {}
}

export class DropDownList extends Control {
    private $main: JQuery;
    private $text: JQuery;
    private $group: JQuery;
    private listDisposable = serialDisposable(null);
    private loadingDisposable = serialDisposable(null);
    private lazyWiringDisposable: IDisposable = emptyDisposable;

    private value = new BehaviorSubject<LookupItem>(null);

    private assignedKeyValues = new BehaviorSubject<string>(null);

    private items = new Lazy(() => this.getLookupItems());
    private filter = new BehaviorSubject<FilterItem>(new FilterItem('', null));
    private allKeys: Observable<number>;
    private enabledStream = new Subject<boolean>();
    private acceptInput = new BehaviorSubject<boolean>(false);

    constructor(args: ControlArgs) {
        super(args);
            
        this.methods.register(this.config.elementName, this);
        this.$element.addClass('control-container dropdownlist');
        this.$main = $('<div />').addClass('noselect').appendTo(this.$element).on('click',() => { });
        this.$text = $('<span />').addClass('noselect dropdownlist-text').appendTo(this.$main);
        var $caret = $('<span />').addClass('dropdownlist-caret fa fa-caret-down').appendTo(this.$main);

        this.applyTooltip(this.$element);

        this.$group = this.$main.add(this.$text).add($caret);
        var deactivations = rxui.groupBlur(this.$group).map(() => ({}));
        var activations = Observable.of({}).concat(deactivations).switchMap(() => rxui.focusin(this.$main).first());

        this.subscriptions = disposableSubscription(
            and(activations.map(() => true).merge(deactivations.map(() => false)), this.enabledStream).subscribe(x => this.acceptInput.next(x)),

            // IE soft/hard focus on div fix
            rxui.mousedownleft(this.$main).delay(0).subscribe(() => this.$main.focus()),

            activations.first().subscribe(() => this.lazyWiring()),
            activations.subscribe(() => this.closedMode()),
            deactivations.filter(() => this.config['content.IsRequiredToAlwaysLoadItemsOnAccess']).subscribe(() => this.items = new Lazy(() => this.getLookupItems())),
            this.assignedKeyValues.filter(x => x === null).subscribe(() => this.select(null, false)),
            this.assignedKeyValues
                .withLatestFrom(this.value,(x, y) => ({ newKey: x, currentItem: y }))
                .filter(x => x.newKey !== null && (x.currentItem === null || x.newKey !== x.currentItem.keyValue))
                .switchMap(x => this.getLookupItemFromMostEfficientSource(x.newKey))
                .subscribe(x => this.select(x, false)),
            this.value.distinctUntilChanged().subscribe(x => this.$element.triggerHandler('content.SelectedValue', x === null ? [null] : [x.keyValue])),
            this.processBindings(this.config),
            this.managePrimaryBinding('content.SelectedValue'),

            activations
                .withLatestFrom(this.value, (x, y) => y)
                .switchMap(x => deactivations.withLatestFrom(this.value, (y, z) => ({ oldValue: x, newValue: z })))
                .filter(x => !this.itemsEquivalent(x.oldValue, x.newValue))
                .subscribe(() => this.triggerEvent('Lost Focus and Value Changed'))
        );

        this.initialize();
    }

    dispose() {
        super.dispose();
        this.listDisposable.dispose();
        this.loadingDisposable.dispose();
        this.lazyWiringDisposable.dispose();
    }
        
    // list rendering and wiring routines
    private openMode(items: LookupItem[], selectedItem: LookupItem) {
        var $list = this.makeList(items);

        var offset = this.$element.offset();
        var availableAbove = offset.top - listMargin;
        var availableBelow = $(window).height() - offset.top - this.$element.height() - listMargin;
        var direction = availableBelow > maxListHeight ? 'down'
            : availableAbove > availableBelow ? 'up'
            : 'down';

        var maxHeight = Math.min(direction === 'up' ? availableAbove : availableBelow, maxListHeight);

        var $popupInner = $('<div />').append($list).css('max-height', maxHeight + 'px');

        var $scrollContainer = $('<div />').append($popupInner);

        // for the pleasure of IE. Honestly, I'm surprised it works, but it does
        $scrollContainer.on('focusin', () => this.$main.focus());

        var $popup = $('<div />').append($scrollContainer).addClass('dropdownlist-list')
            .css('min-width', this.$element.width())
            .appendTo(this.$element.closest('.runtime-application-container'))
            .position({
                my: 'left ' + (direction === 'down' ? 'top' : 'bottom'),
                at: 'left ' + (direction === 'down' ? 'bottom' : 'top'),
                of: this.$element,
                collision: 'flipfit none'
            });

        // this ugliness is to get consistent li sizing, full-row highlight, consistent scrollbar spacing, and proper row padding working simultaneously in Chrome, Edge, FF, Safari
        const verticalScrollbarsRequired = $popupInner[0].scrollHeight > $popupInner[0].clientHeight;
        const maxItemWidth = maxDefaultZero($list.find('li').toArray().map(x => x.scrollWidth));
        $list.find('li').css('width', '100%');
        const targetWidth = Math.max(maxItemWidth + (verticalScrollbarsRequired ? 15 : 0), this.$element.width() - 2);
        $popupInner.width(targetWidth);

        if (selectedItem !== null) {
            ($popupInner.find('li').toArray().map(x => $(x)).find(x => x.data('lookupitem').keyValue === selectedItem.keyValue) || $())
                .addClass('dropdownlist-selected')
                .each((i, e) => this.scrollItemIntoView($(e), $popup));
        }

        var preselection = new BehaviorSubject<LookupItem>(selectedItem);
        var preselectionClass = 'dropdownlist-preselected';

        var closeKeys = this.allKeys.filter(x => x === $.ui.keyCode.ENTER).race(this.allKeys.filter(x => x === $.ui.keyCode.SPACE).whereLatest(this.filter.map(x => x.filter === '')));
        var itemClick = rxui.mousedownleft($popup.find('ul')).do(e => e.preventDefault()).map(x => $(x.target)).map(x => <LookupItem>(x.data('lookupitem')));

        window.setTimeout(() => {
            this.listDisposable.set(disposableSubscription(
                rxui.windowResized().subscribe(() => this.closedMode()),
                disposable(() => $popup.remove()),
                this.allKeys.filter(x => x === $.ui.keyCode.ESCAPE).subscribe(() => this.closedMode()),
                this.navigation(this.allKeys, { 38: -1, 40: 1 }, preselection).subscribe(x => this.manageSelection(items, x.current, x.value, y => preselection.next(y))),
                itemClick.race(closeKeys.withLatestFrom(preselection.asObservable(), (x, y) => y))
                    .withLatestFrom(this.value, (x, y) => ({ newValue: x, oldValue: y }))
                    .subscribe(x => { this.select(x.newValue, !this.itemsEquivalent(x.oldValue, x.newValue)); this.closedMode(); }),
                preselection.pairwise().subscribe(x => {
                    if (x[0] !== null)
                        $list.find('li').toArray().map(x => $(x)).filter(y => this.itemsEquivalent(y.data('lookupitem'), x[0]))[0].removeClass(preselectionClass);

                    if (x[1] != null)
                        $list.find('li').toArray().map(x => $(x)).filter(y => this.itemsEquivalent(y.data('lookupitem'), x[1]))[0].addClass(preselectionClass).each((i, e) => this.scrollItemIntoView($(e), $popup));
                }),
                rxui.clickOutside($list).subscribe(() => this.closedMode()),
                rxui.groupBlur(this.$group.add($scrollContainer)).subscribe(() => this.closedMode()),
                this.filter.filter(x => x.match !== null).subscribe(x => preselection.next(x.match)),
                rxui.scrollOutside($popup).first().subscribe(() => this.closedMode()),
                preselection
            ));
        }, 0);
    }

    private makeList(items: LookupItem[]) {
        return items.length > 0
            ? items.reduce((a, x) => a.append(this.makeListItem(x)), $('<ul />'))
            : $('<span />').addClass('dropdownlist-noresults').text(this.resources.getCoreString('NoResults'));
    }

    private makeListItem(item: LookupItem) {
        return $('<li />').text(item.displayValue).data('lookupitem', item);
    }

    private scrollItemIntoView($item: JQuery, $popup: JQuery) {
        var st = $popup.children('div')[0].scrollTop;
            
        if ($item.position().top + $item.height() > st + $popup.height())
            $item[0].scrollIntoView(false);
        else if (st > $item.position().top)
            $item[0].scrollIntoView(true);
    }

    // selection routines
    private manageSelection(items: LookupItem[], current: LookupItem, nav: number, action: (x: LookupItem) => void) {
        var index = current === null ? -1 : items.findIndex(x => x.keyValue === current.keyValue);

        if (items.length === 0)
            return;

        if (index === -1) {
            if (nav === 1)
                action(items[0]);
        }
        else {
            var newIndex = index + nav;
            if (newIndex < 0 || newIndex >= items.length)
                return;

            action(items[newIndex]);
        }
    }

    private select(item: LookupItem, raiseSelected: boolean) {
        this.$text.text(item === null ? '' : item.displayValue);
        this.value.next(item);
        if (raiseSelected)
            this.triggerEvent('Got Focus and Value Changed');
    }

    // wiring routines
    private lazyWiring() {
        const keysToSwallow = [$.ui.keyCode.LEFT, $.ui.keyCode.RIGHT, $.ui.keyCode.UP, $.ui.keyCode.DOWN, $.ui.keyCode.SPACE, $.ui.keyCode.BACKSPACE];

        this.allKeys = rxui.key(this.$main, e => keysToSwallow.some(x => x === e.keyCode)).share();

        var press = rxui.from<KeyboardEvent>(this.$main, 'keypress').filter(() => this.derivedEnabledState() !== false).share();

        this.lazyWiringDisposable = disposableSubscription(
            press.map(x => String.fromCharCode(x.keyCode).toLowerCase())
                .withLatestFrom(this.filter,(x, y) => ({ newKey: x, existingFilterItem: y, newFilter: y.filter + x }))
                .switchMap(x => this.items.value().map(y => ({ newKey: x.newKey, existingFilterItem: x.existingFilterItem, newFilter: x.newFilter, items: y })))
                .map(x => this.matchFilter(x.existingFilterItem, x.newKey, x.items))
                .filter(x => x !== null)
                .subscribe(x => this.filter.next(x)),

            press.throttleTime(500).subscribe(() => this.filter.next(new FilterItem('', null)))
        );
    }

    private closedMode() {
        var allKeysWhileEnabled = this.allKeys.filter(() => this.derivedEnabledState() !== false);
        var openTrigger =
            this.unitMerge(
                rxui.click(this.$main).filter(() => this.derivedEnabledState() !== false),
                rxui.filteredKeys(this.$main, [$.ui.keyCode.ENTER, $.ui.keyCode.SPACE]).whereLatest(this.filter.map(x => x.filter === ''))
            )
            .filter(() => this.derivedEnabledState() !== false);

        var mouseWheel = this.acceptInput.switchMap(x => x
            ? rxui.from<any>(this.$main, 'mousewheel').do(e => e.preventDefault()).map(x => x.deltaY * -1)
            : Observable.never<number>()
        );

        this.listDisposable.set(disposableSubscription(
            this.withItemsAndValue(openTrigger).subscribe(x => this.openMode(x.items, x.current)),
            this.navigation(allKeysWhileEnabled, { 37: -1, 38: -1, 39: 1, 40: 1 })
                .merge(this.withItemsAndValue(mouseWheel))
                .subscribe(x => this.manageSelection(x.items, x.current, x.value, y => this.select(y, true))),
            allKeysWhileEnabled.filter(x => x === $.ui.keyCode.DELETE).subscribe(() => this.select(null, true)),
            this.filter.filter(x => x.match !== null).subscribe(x => this.select(x.match, true))
        ));
    }

    private showLoading() {
        this.$main.addClass('hide-text');

        var $loading = $('<div />')
            .text(this.resources.getCoreString('XamlLoading'))
            .addClass('dropdownlist-loading')
            .appendTo(this.$element);

        this.loadingDisposable.set(disposable(() => {
            this.$main.removeClass('hide-text');
            $loading.remove();
        }));
    }

    private hideLoading() {
        this.loadingDisposable.set(null);
    }

    'content.SelectedValue'(keyValue: string) {
        this.checkInitialValueReceived();
        this.assignedKeyValues.next(keyValue);
    }

    focus() {
        this.$main.focus();
    }

    applyEnabledState(value: boolean) {
        this.$main.toggleClass('dropdownlist-readonly', !value);
        this.enabledStream.next(value);
    }

    // matching logic
    private matchFilter(existingFilterItem: FilterItem, newKey: string, items: LookupItem[]) {
        var condition = (x: string) => (y: LookupItem) => y.displayValue.toLowerCase().indexOf(x) === 0;
        var candidateFilter = existingFilterItem.filter + newKey;
        var match = items.find(condition(candidateFilter)) || null;
        var nextMatchForExistingFilter = () => {
            var existingMatches = items.filter(condition(existingFilterItem.filter));
            var existingIndex = existingMatches.indexOf(existingFilterItem.match);
            var wrappedMatches = existingMatches.concat(existingMatches);
            const nextItem = wrappedMatches.slice(existingIndex + 1).find(condition(existingFilterItem.filter)) || null;

            return new FilterItem(existingFilterItem.filter, nextItem);
        };

        return match !== null ? new FilterItem(candidateFilter, match)
                : endsWith(existingFilterItem.filter, newKey) ? nextMatchForExistingFilter()
                : null;
    }

    private itemsEquivalent(a: LookupItem, b: LookupItem) {
        return a === null && b === null ? true
                : a === null ? false
                : b === null ? false
                : a.keyValue === b.keyValue;
    }

    // loading routines
    private getLookupItems(): Observable<LookupItem[]> {
        this.showLoading();
        return this.eventAggregator.request(new LookupItemsRequested(this.fieldBindingSource('Content.SelectedValue'), this.fieldContext)).do(() => this.hideLoading());
    }

    private getLookupItemByKey(key: string): Observable<LookupItem> {
        this.showLoading();
        return this.eventAggregator.request(new LookupItemRequested(this.fieldBindingSource('Content.SelectedValue'), this.fieldContext, key)).do(() => this.hideLoading());
    }

    private getLookupItemFromMostEfficientSource(key: string): Observable<LookupItem> {
        return this.items.isValueCreated() ? this.items.value().first().map(x => x.find(y => y.keyValue === key) || null) : this.getLookupItemByKey(key);
    }

    // idiomatic routines
    private navigation(keys: Observable<number>, navMap: any, value?: Observable<LookupItem>) {
        return this.withItemsAndValue(keys.map(x => navMap[x]).filter(x => x !== undefined), value);
    }

    private withItemsAndValue<T>(source: Observable<T>, value?: Observable<LookupItem>) {
        return source
            .switchMap(x => this.items.value().map(y => ({ value: x, items: y })))
            .withLatestFrom(value || this.value,(x, y) => ({ value: x.value, items: x.items, current: y }));
    }

    private unitMerge<T1, T2>(s1: Observable<T1>, s2: Observable<T2>) {
        return s1.map(() => true).merge(s2.map(() => true));
    }

    tabIndexElements() {
        return this.$main;
    }
}

registerBinding('dropdownlist', DropDownList);
